/** 3-component vector as a plain tuple (portable; maps 1:1 to Rust `[f32; 3]`). */
export type Vec3 = [number, number, number]

export const vec3 = {
  create(x = 0, y = 0, z = 0): Vec3 {
    return [x, y, z]
  },
  clone(a: Vec3): Vec3 {
    return [a[0], a[1], a[2]]
  },
  copy(out: Vec3, a: Vec3): Vec3 {
    out[0] = a[0]
    out[1] = a[1]
    out[2] = a[2]
    return out
  },
  set(out: Vec3, x: number, y: number, z: number): Vec3 {
    out[0] = x
    out[1] = y
    out[2] = z
    return out
  },
  add(out: Vec3, a: Vec3, b: Vec3): Vec3 {
    out[0] = a[0] + b[0]
    out[1] = a[1] + b[1]
    out[2] = a[2] + b[2]
    return out
  },
  sub(out: Vec3, a: Vec3, b: Vec3): Vec3 {
    out[0] = a[0] - b[0]
    out[1] = a[1] - b[1]
    out[2] = a[2] - b[2]
    return out
  },
  scale(out: Vec3, a: Vec3, s: number): Vec3 {
    out[0] = a[0] * s
    out[1] = a[1] * s
    out[2] = a[2] * s
    return out
  },
  scaleAndAdd(out: Vec3, a: Vec3, b: Vec3, s: number): Vec3 {
    out[0] = a[0] + b[0] * s
    out[1] = a[1] + b[1] * s
    out[2] = a[2] + b[2] * s
    return out
  },
  dot(a: Vec3, b: Vec3): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
  },
  cross(out: Vec3, a: Vec3, b: Vec3): Vec3 {
    const ax = a[0], ay = a[1], az = a[2]
    const bx = b[0], by = b[1], bz = b[2]
    out[0] = ay * bz - az * by
    out[1] = az * bx - ax * bz
    out[2] = ax * by - ay * bx
    return out
  },
  length(a: Vec3): number {
    return Math.hypot(a[0], a[1], a[2])
  },
  distance(a: Vec3, b: Vec3): number {
    return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
  },
  normalize(out: Vec3, a: Vec3): Vec3 {
    const l = Math.hypot(a[0], a[1], a[2])
    if (l > 0) {
      out[0] = a[0] / l
      out[1] = a[1] / l
      out[2] = a[2] / l
    } else {
      out[0] = out[1] = out[2] = 0
    }
    return out
  },
  lerp(out: Vec3, a: Vec3, b: Vec3, t: number): Vec3 {
    out[0] = a[0] + (b[0] - a[0]) * t
    out[1] = a[1] + (b[1] - a[1]) * t
    out[2] = a[2] + (b[2] - a[2]) * t
    return out
  },
  negate(out: Vec3, a: Vec3): Vec3 {
    out[0] = -a[0]
    out[1] = -a[1]
    out[2] = -a[2]
    return out
  },
  equals(a: Vec3, b: Vec3, eps = 1e-6): boolean {
    return Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps && Math.abs(a[2] - b[2]) <= eps
  },
}
